import { nanoid } from 'nanoid';

import {
  type GridPosition,
  type InteractionSource,
  type SelectionRange,
  type SelectionState
} from '../../../state/types.ts';
import { type ReducerResponse, type SelectionAxis } from '../../types.ts';
import { getRangeMinMax } from '../range/get-range-min-max.ts';

type SplitRangeInMiddleParams = {
  state: SelectionState;
  range: SelectionRange;
  index: number;
  source: InteractionSource;
  axis: SelectionAxis;
};

/**
 * Splits a range in the middle by removing an item, creating two separate ranges.
 * Each new range gets a fresh id (nanoid); the original range id is removed via the delta.
 * @example
 * // User has rows 2-5 selected, clicks row 4 (middle) to deselect it
 * // Before: selections = [{ id: 'old', anchor: { row: 2, col: 0 }, lead: { row: 5, col: 0 } }]
 * // After: selections = [
 * //   { id: '<new1>', anchor: { row: 2, col: 0 }, lead: { row: 3, col: 0 } },
 * //   { id: '<new2>', anchor: { row: 5, col: 0 }, lead: { row: 5, col: 0 } }
 * // ]
 *
 * @returns A reducer response with updated state and delta
 */
export function splitRangeInMiddle({
  state,
  range,
  index,
  source,
  axis
}: SplitRangeInMiddleParams): ReducerResponse {
  const { min } = getRangeMinMax(range, axis);
  const anchorAtStart = range.anchor[axis].index === min;
  const startPosition = anchorAtStart ? range.anchor : range.lead;
  const endPosition = anchorAtStart ? range.lead : range.anchor;

  const firstRangeLead: GridPosition =
    axis === 'row'
      ? { row: { index: index - 1 }, col: startPosition.col }
      : { row: startPosition.row, col: { index: index - 1 } };

  const firstRange: SelectionRange = {
    id: nanoid(),
    anchor: startPosition,
    lead: firstRangeLead
  };

  const secondRangeAnchor: GridPosition =
    axis === 'row'
      ? { row: { index: index + 1 }, col: endPosition.col }
      : { row: endPosition.row, col: { index: index + 1 } };

  const secondRange: SelectionRange = {
    id: nanoid(),
    anchor: anchorAtStart ? secondRangeAnchor : endPosition,
    lead: anchorAtStart ? endPosition : secondRangeAnchor
  };

  const updatedSelections = state.selections
    .filter((r) => r.id !== range.id)
    .concat([firstRange, secondRange]);

  const targetMode = axis === 'row' ? 'row' : 'column';
  const deselectedIndexHadFocus =
    state.focusedCell != null && state.focusedCell[axis].index === index;
  const shouldShiftFocus = deselectedIndexHadFocus;
  const focusedCell = shouldShiftFocus
    ? axis === 'row'
      ? { row: { index: index - 1 }, col: { index: 0 } }
      : { row: { index: 0 }, col: { index: index - 1 } }
    : state.focusedCell;

  const delta: ReducerResponse['delta'] = {
    kind: 'selectionModified',
    source,
    rangeIdsToRemove: [range.id],
    added: [firstRange, secondRange]
  };

  if (shouldShiftFocus) {
    delta.focusChange = {
      prev: state.focusedCell,
      next: focusedCell
    };
  }

  return {
    delta,
    next: {
      ...state,
      mode: targetMode,
      selections: updatedSelections,
      focusedCell,
      lastInteractionSource: source
    }
  };
}
